import { Calculation, CalculationCollection, Invoice, InvoiceListItem, InvoicePreview, InvoiceStatus } from "../../model";
import { Ajax, CrudApiBase } from "../infra";
import { Configs } from "../../logic";

/**
 * Provides read access for authenticated user to access his/her own invoices.
 */
export class Invoices extends CrudApiBase<Invoice, InvoiceListItem> {

  /**
   * For NG1-dependency injection
   * @ignore
   */
  public static $inject = ["AjaxNg1"];

  /** Base URL for details etc. */
  protected baseUrl = "/v03/api/invoices";

  constructor(ajax: Ajax) {
    super(ajax);
  }

  /**
   * Client-side (synchronous) method for getting a new blank item as bases for UI binding.
   * NOTE: At the moment, this method is pretty useless as you cannot save invoice items:
   * You need to create them using createInvoice() method based on a calculation.
   */
  public getBlank(): Invoice {
    return {
      header: {},
      rows: [],
      payer: {},
      recipient: {},
    };
  }

  /**
   * Saving invoices is not supported.
   * @param _item Invoice item (not used)
   * @throws Error indicating operation is not supported
   */
  public save(_item: any): any {
    throw new Error("Not supported.");
  }

  /**
   * Creates invoices to pay a salary calculation.
   * @param calc Calculation for which the invoices are created.
   * @param channel Payment channel to use.
   * @returns Invoices which have been created in the Invoices queue.
   */
  public createInvoices(calc: Calculation, channel: string): Promise<Invoice[]> {
    if (!channel) {
      throw new Error("Payment channel must be specified.");
    }
    calc.info ??= {};
    calc.info.applicationId =  Configs.current?.applicationId;
    const url = `${this.baseUrl}/create/${channel}`;
    return this.ajax.postJSON(url, calc);
  }

  /**
   * Creates invoices to pay a salary calculation(s).
   * @param channel Payment channel to use.
   * @param calcIds One or several identifiers of the Calculation for which the invoices are created.
   * @returns Invoices which have been created in the Invoices queue.
   */
  public createInvoicesById(channel: string, ... calcIds: string[]): Promise<Invoice[]> {
    if (!channel || !calcIds || calcIds.length < 1) {
      throw new Error("One or more calculations Ids and Payment channel must be specified.");
    }
    const url = `${this.baseUrl}/create-by-id/${channel}`;
    return this.ajax.postJSON(url, calcIds);
  }

  /**
   * Creates invoices to pay a set of salary calculation.
   * @param channel Payment channel to use.
   * @param payrollId Identifier for the Payroll for which the invoices are created.
   * @returns Invoices which have been created in the Invoices queue.
   */
  public createInvoicesForPayroll(channel: string, payrollId: string): Promise<Invoice[]> {
    if (!channel || !payrollId) {
      throw new Error("The payroll ID and Payment channel must be specified.");
    }
    const url = `${this.baseUrl}/create-for-payroll/${channel}/${payrollId}`;
    return this.ajax.postJSON(url, null);
  }

  /**
   * Previews invoices to pay a salary calculation(s) and fetches the ones that have already been created.
   * Used for payment dialog preview: Does not yet save the invoices and does not do any other storage saves.
   * @param channel Payment channel to use.
   * @param calcs The calculations for which the invoices are created as preview (not saved to storage).
   * Specified as ID's or unsaved Calculations or Payroll detail items.
   * @returns Invoices (list item + metadata) which have been created for preview (not saved) or that were previously created.
   */
  public previewInvoices(channel: string, calcs: CalculationCollection): Promise<InvoicePreview[]> {
    const itemsCount = (calcs.ids || []).length + (calcs.payrollIds || []).length + (calcs.calcs || []).length + (calcs.payrolls || []).length;
    if (!channel || itemsCount < 1) {
      throw new Error("One or more calculations/payroll/id and Payment channel must be specified.");
    }
    const url = `${this.baseUrl}/preview-and-created/${channel}`;
    return this.ajax.postJSON(url, calcs);
  }

  /**
   * Makes a request for updating the status of the invoice.
   * @param id Identifier for the invoice.
   * @param status The requested new status of the invoice.
   */
  public requestStatusUpdate(id: string, status: InvoiceStatus): Promise<Invoice> {
    const url = `${this.baseUrl}/${id}/status/${status}`;
    return this.ajax.postJSON(url, null);
  }

  /**
   * Returns content (PDF) url for an invoice.
   * @param id The id of the invoice.
   * @param inline If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   */
  public getContentUrl(id: string, inline = false): string {
    return this.ajax.getServerAddress()
      + `${this.baseUrl}/${id}/content?inline=${inline}`
      + `&access_token=${encodeURIComponent(this.ajax.getCurrentToken()!)}`;
  }

  /**
   * Downloads a sepa file for created invoices based on invoice IDs.
   * @param ids Ids of the invoices for the sepa payment.
   * @param payerIbanNumber Payer iban number.
   * @param bankPartyId Payer bank party id. Depends on the bank.
   * @returns A Promise with result data array.
   */
  public getSepaByIds(ids: string[], payerIbanNumber?: string, bankPartyId?: string): Promise<string | undefined> {
    if (ids == null || ids.length === 0) {
      return Promise.resolve(undefined);
    }
    const idsPart = ids.map(x => "ids=" + encodeURIComponent("" + x)).join("&");
    let url = `${this.baseUrl}/sepa?${idsPart}`;
    if (payerIbanNumber) {
      url += "&payerIbanNumber=" + encodeURIComponent(payerIbanNumber);
    }
    if (bankPartyId) {
      url += "&bankPartyId=" + encodeURIComponent(bankPartyId);
    }
    return this.ajax.getHTML(url);
  }

  /**
   * Retrieves the content (PDF) for an invoice by ID.
   * @param id - The ID of the invoice
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A Promise that resolves to a Blob containing the invoice PDF
   */
  public getContent(id: string, inline?: boolean): Promise<Blob>;
  /**
   * Retrieves the content (PDF) for an invoice object.
   * @param invoice - The Invoice object
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A Promise that resolves to a Blob containing the invoice PDF
   */
  public getContent(invoice: Invoice, inline?: boolean): Promise<Blob>;

  /**
   * Implementation of getContent() that handles both string ID and Invoice object inputs.
   * For string IDs, makes a GET request to fetch the invoice PDF.
   * For Invoice objects, makes a POST request with the full invoice data.
   * @param idOrInvoice - Either an invoice ID string or Invoice object
   * @param inline - If true, returns content inline in browser. If false, returns as download attachment.
   * @returns Promise resolving to a Blob containing the invoice PDF
   */
  public getContent(idOrInvoice: string | Invoice, inline = false): Promise<Blob> {
    if(typeof idOrInvoice === "string") {
      return this.ajax.getBlob(`${this.baseUrl}/${idOrInvoice}/content?inline=${inline}`).then(response => response.data);
    } else {
      return this.ajax.postBlob(`${this.baseUrl}/content?inline=${inline}`, idOrInvoice).then(response => response.data);
    }
  }

  /**
   * Gets the URL for downloading a SEPA file for the specified invoices.
   * @param idsOrInvoices - Array of invoice IDs or Invoice objects to include in the SEPA file
   * @param payerIbanNumber - Optional IBAN number of the payer
   * @param bankPartyId - Optional bank party ID of the payer
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download). Defaults to false.
   * @returns The complete URL for downloading the SEPA file, including server address and access token
   */
  public getSepaUrl(idsOrInvoices: string[] | Invoice[], payerIbanNumber?: string, bankPartyId?: string, inline = false): string {
    const ids = idsOrInvoices.map(item => typeof item === "string" ? item : item.id);
    const idsPart = ids.map(id => `ids=${encodeURIComponent(id)}`).join("&");
    let url = `${this.baseUrl}/sepa?${idsPart}&inline=${inline}`;

    if (payerIbanNumber) {
      url += `&payerIbanNumber=${encodeURIComponent(payerIbanNumber)}`;
    }
    if (bankPartyId) {
      url += `&bankPartyId=${encodeURIComponent(bankPartyId)}`;
    }
    return this.ajax.getServerAddress()
      + `${url}`
      + `&access_token=${encodeURIComponent(this.ajax.getCurrentToken()!)}`;
  }

  /**
   * Retrieves a SEPA file for the specified invoice IDs.
   * @param ids - Array of invoice IDs to include in the SEPA file
   * @param payerIbanNumber - Optional IBAN number of the payer
   * @param bankPartyId - Optional bank party ID of the payer
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A Promise that resolves to a Blob containing the SEPA file content
   */
  public getSepa(ids: string[], payerIbanNumber?: string, bankPartyId?: string, inline?: boolean): Promise<Blob>;
  /**
   * Retrieves a SEPA file for the specified invoice objects.
   * @param invoices - Array of Invoice objects to include in the SEPA file
   * @param payerIbanNumber - Optional IBAN number of the payer
   * @param bankPartyId - Optional bank party ID of the payer
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A Promise that resolves to a Blob containing the SEPA file content
   */
  public getSepa(invoices: Invoice[], payerIbanNumber?: string, bankPartyId?: string, inline?: boolean): Promise<Blob>;

  /**
   * Implementation of the SEPA file retrieval for both string IDs and Invoice objects.
   * @param idsOrInvoices - Array of invoice IDs or Invoice objects to include in the SEPA file
   * @param payerIbanNumber - Optional IBAN number of the payer
   * @param bankPartyId - Optional bank party ID of the payer
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download). Defaults to false.
   * @returns A Promise that resolves to a Blob containing the SEPA file content
   */
  public getSepa(idsOrInvoices: string[] | Invoice[], payerIbanNumber?: string, bankPartyId?: string, inline = false): Promise<Blob> {
    const isStringArray = idsOrInvoices.every(item => typeof item === "string");
    if(isStringArray) {
      const idsPart = idsOrInvoices.map(id => `ids=${encodeURIComponent(id)}`).join("&");
      let url = `${this.baseUrl}/sepa?${idsPart}&inline=${inline}`;

      if (payerIbanNumber) {
        url += `&payerIbanNumber=${encodeURIComponent(payerIbanNumber)}`;
      }
      if (bankPartyId) {
        url += `&bankPartyId=${encodeURIComponent(bankPartyId)}`;
      }
      return this.ajax.getBlob(url).then(response => response.data);
    } else {
      const url = `${this.baseUrl}/sepa?inline=${inline}`;
      return this.ajax.postBlob(url, {
        idsOrInvoices,
        payerIbanNumber,
        bankPartyId,
      }).then(response => response.data);
    }
  }

  /**
   * Gets the URL for downloading a finvoice file for an invoice.
   * @param id - The ID of the invoice to get the finvoice URL for.
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns The complete URL for downloading the finvoice file, including server address and access token.
   */
  public getFinvoiceUrl(id: string, inline = false): string {
    return this.ajax.getServerAddress()
      + `${this.baseUrl}/${id}/finvoice?inline=${inline}`
      + `&access_token=${encodeURIComponent(this.ajax.getCurrentToken()!)}`;
  }

  /**
   * Gets a finvoice file for an invoice using the invoice ID.
   * @param id - The ID of the invoice to get the finvoice for
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A Promise that resolves to a Blob containing the finvoice file
   */
  public getFinvoice(id: string, inline?: boolean): Promise<Blob>;
  /**
   * Gets a finvoice file by posting the invoice object.
   * @param invoice - The Invoice object to generate the finvoice for
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download).
   * @returns A Promise that resolves to a Blob containing the finvoice file
   */
  public getFinvoice(invoice: Invoice, inline?: boolean): Promise<Blob>;

  /**
   * Implementation of getFinvoice() that handles both string ID and Invoice object inputs.
   * @param idOrInvoice - Either an invoice ID string or Invoice object
   * @param inline - If true, returns the content as inline (disposition) in browser.
   * If false, the content is returned as an attachment (download). Defaults to false.
   * @returns A Promise that resolves to a Blob containing the finvoice file
   */
  public getFinvoice(idOrInvoice: string | Invoice, inline = false): Promise<Blob> {
    if(typeof idOrInvoice === "string") {
      const url = `${this.baseUrl}/${idOrInvoice}/finvoice?inline=${inline}`;
      return this.ajax.getBlob(url).then(response => response.data);
    } else {
      const url = `${this.baseUrl}/finvoice?inline=${inline}`;
      return this.ajax.postBlob(url, idOrInvoice).then(response => response.data);
    }
  }

  /**
   * Sends an e-invoice for an invoice.
   * @param id - The ID of the invoice to send the e-invoice for
   * @param receiver - The receiver of the e-invoice
   * @param intermediator - The intermediator of the e-invoice
   * @returns A Promise that resolves to the result of the e-invoice sending process
   */
  public sendEInvoice(id: string, receiver: string, intermediator: string): Promise<any> {
    const url = `${this.baseUrl}/${id}/einvoice?receiver=${encodeURIComponent(receiver)}&intermediator=${encodeURIComponent(intermediator)}`;
    return this.ajax.postJSON(url, {});
  }
}
